/**
 * Unread-count badge for bell buttons. Positioned absolutely, so the parent
 * must be `relative`. Renders nothing when the count is zero.
 *
 * Accessibility: the parent bell button carries an explicit
 * `aria-label="Toggle notifications"`, and an explicit label overrides
 * descendant content in the accessible-name computation — so text in here is
 * never announced on its own. The parent therefore points `aria-describedby` at
 * this badge's `id`, which screen readers read after the button's name.
 *
 * The count is deliberately NOT folded into the button's `aria-label`, even
 * though that would be the more conventional pattern: that label string is load
 * bearing. `features/notifications/Notifications.tsx` separately uses the
 * button's `data-notification-toggle` marker as its click-away guard, while
 * live Playwright specs select this accessible name exactly. Making the label
 * dynamic would break those accessibility contracts.
 */
export const NotificationBadge = ({ count, id }: { count: number; id?: string }) => {
  if (count <= 0) return null;

  return (
    <span
      id={id}
      // [MCV-1578] `bg-info`, not `bg-danger`: an unread count is information,
      // not an error. The danger tone reused the error token for something that
      // is not an error state, and it is what made having notifications look
      // like a bad thing. Geometry is unchanged — `min-w-4` already floors a
      // single digit at 16px, so the badge was and remains a true circle until
      // the count needs two digits.
      className="absolute -top-1 -right-1 flex items-center justify-center min-w-4 h-4 px-1 text-[10px] font-bold text-white bg-info rounded-pill"
    >
      <span aria-hidden="true">{count > 99 ? '99+' : count}</span>
      <span className="sr-only">{count} unread notifications</span>
    </span>
  );
};
